<!--
  PrototypesIndex.vue -- landing page for internal integration prototypes.

  A simple directory of throwaway capability-check / demo surfaces that
  live outside the product proper. Reachable from the SuperAdmin
  Operations sidebar (Prototypes section) and at /admin/prototypes.

  Each card links to a prototype and carries a status chip so the demo
  state is honest at a glance (Live vs. work-in-progress / blocked).

  These are NOT product features. They exist to de-risk vendor
  integrations before we commit build time to a real surface.
-->
<template>
  <div class="proto-index" data-testid="prototypes-index">
    <header>
      <h1 class="proto-index__title">Prototypes</h1>
      <p class="proto-index__sub">
        Internal integration prototypes -- throwaway surfaces used to
        de-risk vendor integrations before building the real thing. Not
        product features.
      </p>
    </header>

    <ul class="proto-index__list">
      <li
        v-for="p in prototypes"
        :key="p.name"
        class="proto-index__item"
        :data-testid="`prototype-card-${p.testid}`"
      >
        <router-link :to="p.to" class="proto-index__card">
          <div class="proto-index__card-head">
            <span class="proto-index__card-title">{{ p.title }}</span>
            <span
              class="proto-index__chip"
              :class="`proto-index__chip--${p.statusKind}`"
              :data-testid="`prototype-status-${p.testid}`"
            >{{ p.status }}</span>
          </div>
          <p class="proto-index__card-desc">{{ p.description }}</p>
        </router-link>
      </li>
    </ul>
  </div>
</template>

<script>
export default {
  name: 'PrototypesIndex',
  data() {
    return {
      prototypes: [
        {
          name: 'wilcom-lettering',
          testid: 'wilcom-lettering',
          title: 'Wilcom Lettering',
          to: '/admin/prototypes/wilcom-lettering',
          status: 'Live',
          statusKind: 'live',
          description:
            'Render lettering text as a real embroidery TrueView via the ' +
            'Wilcom EWA. Returns stitch count, colors, and size for ' +
            'decoration pricing. Credentials proxied server-side.',
        },
        {
          name: 'artifi-designer',
          testid: 'artifi-designer',
          title: 'Artifi Designer (Phase B)',
          to: '/admin/prototypes/artifi-designer',
          status: 'WIP -- blocked on vendor',
          statusKind: 'wip',
          description:
            'Interactive Artifi Designer embed spike. The bundle loads ' +
            'and initializes against Website 445, but the canvas does not ' +
            'yet paint -- blocked on an Amla integration doc (PR #1773).',
        },
        {
          name: 'wilcom-emb-render-stage',
          testid: 'wilcom-emb-render-stage',
          title: 'Wilcom .emb / .dst Render Stage',
          to: '/admin/prototypes/wilcom-emb-render-stage',
          status: 'Live',
          statusKind: 'live',
          description:
            'Upload an .emb or .dst design and render it through the ' +
            'same service the artwork preview drain uses. Shows ' +
            'thumbnail + full-size + stitch count + the exact Wilcom ' +
            'request/response recipe -- a durable debugging tool, not a ' +
            'throwaway spike.',
        },
        {
          name: 'tiptap-editor',
          testid: 'tiptap-editor',
          title: 'TipTap Rich-Text Editor (CKEditor replacement eval)',
          to: '/admin/prototypes/tiptap-editor',
          status: 'Live',
          statusKind: 'live',
          description:
            'CKEditor 5 (GPL) -> TipTap (MIT) evaluation. Full toolbar ' +
            '(H2/H3, bold, italic, links, lists, blockquote, list ' +
            'indent/outdent, undo/redo) plus inline images uploaded to ' +
            '/api/upload_image, with a live editor.getHTML() pane showing ' +
            'the HTML production would store + sanitize. Proves the swap ' +
            'needs no second swap to add images.',
        },
      ],
    }
  },
}
</script>

<style scoped>
.proto-index__title {
  font-size: 22px;
  font-weight: 600;
  margin: 0 0 6px;
  color: #1a202c;
}
.proto-index__sub {
  font-size: 14px;
  color: #4a5568;
  margin: 0 0 24px;
  max-width: 66ch;
}
.proto-index__list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 16px;
}
.proto-index__card {
  display: block;
  padding: 18px 20px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  text-decoration: none;
  color: inherit;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.proto-index__card:hover {
  border-color: #cbd5e0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}
.proto-index__card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}
.proto-index__card-title {
  font-size: 16px;
  font-weight: 600;
  color: #1a202c;
}
.proto-index__chip {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 999px;
  white-space: nowrap;
}
.proto-index__chip--live {
  background: #c6f6d5;
  color: #22543d;
}
.proto-index__chip--wip {
  background: #feebc8;
  color: #7b341e;
}
.proto-index__card-desc {
  font-size: 13px;
  color: #4a5568;
  margin: 0;
  line-height: 1.5;
}
</style>
